import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Compass, ShoppingBag, Users, Megaphone, TrendingUp, Network, ArrowRight, Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/retention";

/**
 * Phase 11.7 — How The Lifted Ecosystem Works.
 *
 * Replaces the generic-network framing with a clear 6-step funnel:
 * Discovery → Customer → Affiliate → Awareness → Expansion → More customers.
 * Fires `ecosystem_view` once on intersection so admin reports can track
 * funnel comprehension separately from page load.
 */
const STEPS = [
  { n: "01", icon: Compass,    title: "People discover Lifted.",                       blurb: "Capsule drops, lifestyle content, real customers in real cities." },
  { n: "02", icon: ShoppingBag,title: "People become customers.",                      blurb: "Shopping the gear, accessories, and supplies that fit the lifestyle." },
  { n: "03", icon: Users,      title: "Some customers become affiliates.",             blurb: "Sharing the brand they already love — naturally, not transactionally." },
  { n: "04", icon: Megaphone,  title: "Affiliates help grow awareness.",               blurb: "Content. Community. Word-of-mouth. The marketing engine in motion." },
  { n: "05", icon: TrendingUp, title: "Growth attracts stores, drivers, and franchise interest.", blurb: "More attention pulls in the operators who keep the ecosystem moving." },
  { n: "06", icon: Network,    title: "The ecosystem expands.",                        blurb: "Each role strengthens the next. Community is the outcome, not a role." },
];

export default function EcosystemFlowSection({ id = "ecosystem-flow", showCta = true }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) { trackEvent("ecosystem_view"); return; }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { trackEvent("ecosystem_view"); io.disconnect(); break; }
      }
    }, { threshold: 0.2 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={id}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16"
    >
      <div className="text-center max-w-2xl mx-auto fade-up">
        <div className="eyebrow inline-flex items-center gap-2 justify-center"><Sparkles className="h-3 w-3" /> How it works</div>
        <h2 className="font-anton text-4xl sm:text-5xl uppercase mt-3 leading-[0.95]">How the Lifted<br /><span className="text-lifted-green">ecosystem grows.</span></h2>
        <p className="text-lifted-dim text-sm sm:text-base mt-4">
          Six simple steps. One connected loop. Customers become affiliates. Affiliates help everyone grow.
        </p>
      </div>
      <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${id}-steps`}>
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <li key={s.n} data-testid={`${id}-step-${s.n}`} className="glass-panel p-5">
              <div className="flex items-center gap-3">
                <div className="font-anton text-3xl text-lifted-green leading-none">{s.n}</div>
                <Icon className="h-5 w-5 text-lifted-text" aria-hidden="true" />
              </div>
              <div className="font-anton uppercase text-base mt-3 leading-tight">{s.title}</div>
              <div className="text-[0.72rem] text-lifted-dim mt-1.5 leading-relaxed">{s.blurb}</div>
            </li>
          );
        })}
      </ol>
      {showCta ? (
        <div className="mt-8 flex justify-center">
          <Link
            to="/ecosystem"
            data-testid={`${id}-cta`}
            className="btn-lifted"
            onClick={() => trackEvent("ecosystem_preview_click", { surface: "flow_section" })}
          >
            Explore the full ecosystem <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      ) : null}
    </section>
  );
}
